import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import emailjs from '@emailjs/browser';
import { BrowserRouter, Link, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import PortfolioV2 from './PortfolioV2';
import EstimatorPage from './pages/EstimatorPage';
import PricingV2 from './pages/PricingV2';
import AtelierDHeloise from './pages/AtelierDHeloise';
import { LanguageProvider, useLang } from './i18n';
import CGU from './pages/CGU';
import CGV from './pages/CGV';
import ServicePage from './pages/ServicePage';
import DeveloppeurWebQuimper from './pages/DeveloppeurWebQuimper';
import DeveloppeurWebFrance from './pages/DeveloppeurWebFrance';
import MentionsLegales from './pages/MentionsLegales';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import AnalyticsConsent from './components/AnalyticsConsent';

const languages = ['fr', 'en', 'pt'];

const copy = {
  fr: {
    langLabel: 'Langue',
    brandRole: 'Développeur web freelance',
    heroSlides: [
      {
        eyebrow: '01 · Créer',
        title: 'Vous avez une activité, mais pas encore le bon site.',
        text: 'Site vitrine, boutique, réservation ou première application : on part de votre besoin, même si vous n’avez encore rien de technique.',
        micro: 'Idéal pour lancer une activité, remplacer une présence bricolée ou créer une première version propre.',
        cta: 'Créer mon projet',
        image: '/atelier-dheloise.png',
      },
      {
        eyebrow: '02 · Simplifier',
        title: 'Votre activité fonctionne. Votre site peut maintenant vous faire gagner du temps.',
        text: 'Réservations, paiements, devis, commandes, emails, stock ou back-office : je transforme les tâches répétitives en parcours plus simples.',
        micro: 'On garde ce qui fonctionne et on ajoute uniquement ce qui apporte une vraie utilité au quotidien.',
        cta: 'Simplifier mon activité',
        image: '/previews/stockscan.png',
      },
      {
        eyebrow: '03 · Reprendre',
        title: 'Le projet existe déjà, mais il bloque avant la ligne d’arrivée.',
        text: 'Projet commencé avec une IA, du no-code ou un autre développeur : je peux reprendre l’existant, prioriser et l’amener vers une version exploitable.',
        micro: 'Une reprise commence par comprendre ce qui existe avant de promettre de tout refaire.',
        cta: 'Faire reprendre mon projet',
        image: '/previews/samass.png',
      },
    ],
    heroTitle: 'Réservations, paiements, devis, commandes : je transforme les tâches manuelles en outils web simples.',
    heroSubtitle:
      'J’accompagne les indépendants et petites entreprises avec des sites et outils métier utiles. Je peux aussi reprendre un projet commencé avec une IA, du no-code ou un autre développeur.',
    heroMicro:
      'Vous m’expliquez ce qui vous prend du temps aujourd’hui. Je vous propose une première version réaliste, un budget clair et un plan de livraison.',
    ctaProjects: 'Voir les projets',
    ctaService: 'Détails du service',
    ctaContact: 'Parler du projet',
    ctaAbout: 'À propos',

    projectsTitle: 'Projets',
    projectsIntro:
      'Un cas client réel et des produits en ligne : boutique, réservation, paiement, stock, emails et back-office.',
    needLabel: 'Le besoin',
    solutionLabel: 'Le travail réalisé',
    showsLabel: 'Le résultat',
    stackLabel: 'Stack',
    btnDemo: 'Voir le projet',
    btnCaseStudy: 'Voir le cas client',
    btnGithub: 'Code source',

    situationsTitle: 'Les problèmes que je peux prendre en charge',
    situationsIntro:
      'Pas besoin d’être technique ni d’avoir un cahier des charges. On part de votre activité et de ce qui vous fait perdre du temps.',
    situations: [
      {
        title: 'Le site est déjà en ligne',
        text: 'Certaines actions prennent encore trop de temps.',
      },
      {
        title: 'Un outil existe',
        text: 'Il manque encore des parties importantes.',
      },
      {
        title: 'Tout est encore manuel',
        text: 'Les réservations, paiements, emails ou formulaires sont encore gérés à la main.',
      },
      {
        title: 'Une première version a été commencée',
        text: 'Il faut la rendre plus claire, plus stable ou plus simple à utiliser.',
      },
    ],

    techTitle: 'Outils techniques',
    techIntro:
      'Les outils changent selon le projet. Voici ceux que j’utilise le plus souvent.',
    techGroups: [
      { title: 'Frontend', items: ['React', 'Next.js', 'TypeScript', 'Tailwind'] },
      { title: 'Backend', items: ['Python', 'Django', 'Django REST Framework', 'FastAPI'] },
      { title: 'Données et services', items: ['PostgreSQL', 'SQLite', 'Stripe', 'SendGrid'] },
      { title: 'Déploiement', items: ['Vercel', 'Render', 'Heroku', 'GitHub'] },
    ],

    aboutTitle: 'À propos',
    aboutParagraphs: [
      'Je suis Alioune Seck, développeur web freelance. Je travaille sur des projets où le code doit servir quelque chose de concret : réserver, gérer, suivre, vendre ou faire gagner du temps.',
      'J’ai travaillé sur des sites, des outils internes, des intégrations API et mes propres produits. Reprendre un projet déjà commencé est souvent plus difficile que partir de zéro, et c’est précisément le type de situation que je sais traiter.',
    ],
    aboutJourneyTitle: 'Parcours',
    aboutJourney: [
      {
        title: 'Fondateur, StockScan',
        period: '2025 à aujourd’hui',
        text: 'SaaS d’inventaire en ligne : scan, stock, exports, étiquettes et suivi terrain.',
      },
      {
        title: 'Développeur web, LATELIER',
        period: 'Sept. 2024 à Mai 2025',
        text: 'Sites et outils web pour PME, intégrations API, SEO technique et accessibilité.',
      },
      {
        title: 'Développeur full-stack, IPI',
        period: 'Sept. 2022 à Août 2023',
        text: 'Application interne React et Django REST.',
      },
      {
        title: 'Développeur frontend, Avisen',
        period: 'Mai 2022 à Sept. 2022',
        text: 'Intégration Figma, responsive et corrections UI.',
      },
    ],
    aboutTodayTitle: 'Aujourd’hui',
    aboutToday:
      'Je travaille surtout sur des projets courts ou bien définis : terminer une app, corriger un outil, ajouter une réservation, brancher un paiement, mettre en ligne ou rendre un back-office plus simple à utiliser.',
    aboutNote: 'Basé à Quimper · disponible dans le Finistère et à distance',
    closeAbout: 'Fermer',

    contactTitle: 'Parlez-moi de ce que vous avez.',
    contactIntro:
      'Pas besoin d’un cahier des charges. Un lien, une capture, une liste de bugs ou quelques lignes suffisent pour savoir si je peux aider.',
    phName: 'Votre nom',
    phEmail: 'Votre email',
    phPhone: 'Téléphone ou WhatsApp (optionnel)',
    phMessage: 'Expliquez simplement ce que vous avez, ce qui ne va pas, ou ce que vous voulez mettre en ligne.',
    submit: 'Envoyer le message',
    msgSuccess: 'Votre message a bien été envoyé. Vous recevrez aussi un email de confirmation.',
    msgError: "Erreur lors de l’envoi du message. Vérifiez votre configuration EmailJS.",

    navProjects: 'Projets',
    navContact: 'Contact',
    footerIdentity:
      'Développeur web freelance · Applications web, reprises d’existant, outils métiers',
    footerNavTitle: 'Navigation',
    footerLegalTitle: 'Légal',
    footerContactTitle: 'Contact',
    footerPricing: 'Tarifs',
    footerServicesTitle: 'Services',
    footerServiceCreation: 'Création de site',
    footerServiceBooking: 'Réservation & paiement',
    footerServiceTakeover: 'Reprise de projet',
    footerLocal: 'Développeur web Quimper',
    footerMentions: 'Mentions légales',
    footerPrivacy: 'Confidentialité',
    footerCgu: 'CGU',
    footerSiren: 'SIREN : 995 288 438',
    footerApe: 'APE : 62.01Z',
    footerCopyright: '© 2026 Alioune Seck. Tous droits réservés.',
  },

  en: {
    langLabel: 'Language',
    brandRole: 'Freelance web developer',
    heroSlides: [
      {
        eyebrow: '01 · Build',
        title: 'You have a business, but not the right website yet.',
        text: 'Business website, shop, booking flow or first web app: we start from your need, even if you have nothing technical yet.',
        micro: 'A good fit for launching a business, replacing a makeshift online presence or shipping a solid first version.',
        cta: 'Start my project',
        image: '/atelier-dheloise.png',
      },
      {
        eyebrow: '02 · Simplify',
        title: 'Your business works. Your website can now save you time.',
        text: 'Bookings, payments, quotes, orders, emails, stock or admin: I turn repetitive tasks into simpler workflows.',
        micro: 'We keep what already works and add only what brings real day-to-day value.',
        cta: 'Simplify my workflow',
        image: '/previews/stockscan.png',
      },
      {
        eyebrow: '03 · Take over',
        title: 'The project already exists, but it is stuck before the finish line.',
        text: 'Started with AI, no-code or another developer: I can take over the existing project, prioritize it and move it toward a usable release.',
        micro: 'A takeover starts by understanding what exists before promising to rebuild everything.',
        cta: 'Take over my project',
        image: '/previews/samass.png',
      },
    ],
    heroTitle: 'Bookings, payments, quotes and orders: I turn manual work into simple web tools.',
    heroSubtitle:
      'I help independent professionals and small businesses with useful websites and business tools. I can also take over a project started with AI, no-code or another developer.',
    heroMicro:
      'Tell me what takes time in your business today. I will suggest a realistic first version, a clear budget and a delivery plan.',
    ctaProjects: 'View projects',
    ctaService: 'Service details',
    ctaContact: 'Discuss the project',
    ctaAbout: 'About',

    projectsTitle: 'Projects',
    projectsIntro:
      'One real client case and live products: shop, booking, payment, stock, emails and back-office.',
    needLabel: 'The need',
    solutionLabel: 'Work delivered',
    showsLabel: 'Result',
    stackLabel: 'Stack',
    btnDemo: 'View project',
    btnCaseStudy: 'View case study',
    btnGithub: 'Source code',

    situationsTitle: 'Problems I can take off your plate',
    situationsIntro:
      'You do not need to be technical or have a full brief. We start from your business and what currently wastes your time.',
    situations: [
      {
        title: 'The site is already online',
        text: 'Some actions still take too much time.',
      },
      {
        title: 'A tool already exists',
        text: 'Important parts are still missing.',
      },
      {
        title: 'Everything is still manual',
        text: 'Bookings, payments, emails or forms are still handled by hand.',
      },
      {
        title: 'A first version has been started',
        text: 'It needs to become clearer, more stable or easier to use.',
      },
    ],

    techTitle: 'Technical tools',
    techIntro:
      'Tools change depending on the project. These are the ones I use most often.',
    techGroups: [
      { title: 'Frontend', items: ['React', 'Next.js', 'TypeScript', 'Tailwind'] },
      { title: 'Backend', items: ['Python', 'Django', 'Django REST Framework', 'FastAPI'] },
      { title: 'Data & services', items: ['PostgreSQL', 'SQLite', 'Stripe', 'SendGrid'] },
      { title: 'Deployment', items: ['Vercel', 'Render', 'Heroku', 'GitHub'] },
    ],

    aboutTitle: 'About',
    aboutParagraphs: [
      'I am Alioune Seck, a freelance web developer. I work on projects where code has to do something useful: book, manage, track, sell or save time.',
      'I have worked on websites, internal tools, API integrations and my own products. Taking over an existing project is often harder than starting from scratch, and that is exactly the kind of work I can handle.',
    ],
    aboutJourneyTitle: 'Background',
    aboutJourney: [
      {
        title: 'Founder, StockScan',
        period: '2025 to present',
        text: 'Live inventory SaaS: scanning, stock, exports, labels and field use.',
      },
      {
        title: 'Web developer, LATELIER',
        period: 'Sep. 2024 to May 2025',
        text: 'Websites and tools for SMEs, API integrations, technical SEO and accessibility.',
      },
      {
        title: 'Full-stack developer, IPI',
        period: 'Sep. 2022 to Aug. 2023',
        text: 'Internal application with React and Django REST.',
      },
      {
        title: 'Frontend developer, Avisen',
        period: 'May 2022 to Sep. 2022',
        text: 'Figma integration, responsive work and UI fixes.',
      },
    ],
    aboutTodayTitle: 'Today',
    aboutToday:
      'I mostly work on short or scoped projects: finish an app, fix a tool, add booking, connect payment, deploy, or make an admin area easier to use.',
    aboutNote: 'Based in Quimper, France · available remotely',
    closeAbout: 'Close',

    contactTitle: 'Tell me what you have.',
    contactIntro:
      'You do not need a full brief. A link, screenshot, bug list or a few lines are enough to see whether I can help.',
    phName: 'Your name',
    phEmail: 'Your email',
    phPhone: 'Phone or WhatsApp (optional)',
    phMessage: 'Explain what you have, what is not working, or what you want to put online.',
    submit: 'Send message',
    msgSuccess: 'Your message has been sent. You will also receive a confirmation email.',
    msgError: 'There was an error while sending the form. Please check your EmailJS configuration.',

    navProjects: 'Projects',
    navContact: 'Contact',
    footerIdentity:
      'Freelance web developer · Web applications, existing app takeovers, business tools',
    footerNavTitle: 'Navigation',
    footerLegalTitle: 'Legal',
    footerContactTitle: 'Contact',
    footerPricing: 'Pricing',
    footerServicesTitle: 'Services',
    footerServiceCreation: 'Website creation',
    footerServiceBooking: 'Booking & payment',
    footerServiceTakeover: 'Project takeover',
    footerLocal: 'Web developer in Quimper',
    footerMentions: 'Legal notice',
    footerPrivacy: 'Privacy',
    footerCgu: 'Terms',
    footerSiren: 'SIREN: 995 288 438',
    footerApe: 'APE: 62.01Z',
    footerCopyright: '© 2026 Alioune Seck. All rights reserved.',
  },

  pt: {
    langLabel: 'Idioma',
    brandRole: 'Desenvolvedor web freelance',
    heroSlides: [
      {
        eyebrow: '01 · Criar',
        title: 'Você tem uma atividade, mas ainda não tem o site certo.',
        text: 'Site institucional, loja, reservas ou primeira aplicação: começamos pela sua necessidade, mesmo que você ainda não tenha nada técnico.',
        micro: 'Ideal para lançar uma atividade, substituir uma presença improvisada ou criar uma primeira versão sólida.',
        cta: 'Criar meu projeto',
        image: '/atelier-dheloise.png',
      },
      {
        eyebrow: '02 · Simplificar',
        title: 'Sua atividade funciona. Seu site agora pode fazer você ganhar tempo.',
        text: 'Reservas, pagamentos, orçamentos, pedidos, emails, estoque ou administração: transformo tarefas repetitivas em fluxos mais simples.',
        micro: 'Mantemos o que funciona e adicionamos apenas o que traz utilidade real no dia a dia.',
        cta: 'Simplificar minha atividade',
        image: '/previews/stockscan.png',
      },
      {
        eyebrow: '03 · Retomar',
        title: 'O projeto já existe, mas travou antes da linha de chegada.',
        text: 'Projeto iniciado com IA, no-code ou outro desenvolvedor: posso retomar o existente, priorizar e levá-lo a uma versão utilizável.',
        micro: 'Uma retomada começa por entender o que existe antes de prometer refazer tudo.',
        cta: 'Retomar meu projeto',
        image: '/previews/samass.png',
      },
    ],
    heroTitle: 'Reservas, pagamentos, orçamentos e pedidos: transformo tarefas manuais em ferramentas web simples.',
    heroSubtitle:
      'Ajudo profissionais independentes e pequenas empresas com sites e ferramentas úteis. Também posso retomar um projeto iniciado com IA, no-code ou outro desenvolvedor.',
    heroMicro:
      'Você me explica o que toma tempo hoje. Eu proponho uma primeira versão realista, um orçamento claro e um plano de entrega.',
    ctaProjects: 'Ver projetos',
    ctaService: 'Detalhes do serviço',
    ctaContact: 'Falar do projeto',
    ctaAbout: 'Sobre',

    projectsTitle: 'Projetos',
    projectsIntro:
      'Um caso real de cliente e produtos online: loja, reservas, pagamento, estoque, emails e back-office.',
    needLabel: 'A necessidade',
    solutionLabel: 'O trabalho realizado',
    showsLabel: 'O resultado',
    stackLabel: 'Stack',
    btnDemo: 'Ver projeto',
    btnCaseStudy: 'Ver caso do cliente',
    btnGithub: 'Código fonte',

    situationsTitle: 'Problemas que posso assumir',
    situationsIntro:
      'Você não precisa ser técnico nem ter um briefing completo. Começamos pela sua atividade e pelo que faz você perder tempo hoje.',
    situations: [
      {
        title: 'O site já está online',
        text: 'Algumas ações ainda tomam tempo demais.',
      },
      {
        title: 'Uma ferramenta já existe',
        text: 'Ainda faltam partes importantes.',
      },
      {
        title: 'Tudo ainda é manual',
        text: 'Reservas, pagamentos, emails ou formulários ainda são geridos à mão.',
      },
      {
        title: 'Uma primeira versão foi iniciada',
        text: 'Ela precisa ficar mais clara, mais estável ou mais simples de usar.',
      },
    ],

    techTitle: 'Ferramentas técnicas',
    techIntro:
      'As ferramentas mudam conforme o projeto. Estas são as que uso com mais frequência.',
    techGroups: [
      { title: 'Frontend', items: ['React', 'Next.js', 'TypeScript', 'Tailwind'] },
      { title: 'Backend', items: ['Python', 'Django', 'Django REST Framework', 'FastAPI'] },
      { title: 'Dados & serviços', items: ['PostgreSQL', 'SQLite', 'Stripe', 'SendGrid'] },
      { title: 'Deploy', items: ['Vercel', 'Render', 'Heroku', 'GitHub'] },
    ],

    aboutTitle: 'Sobre',
    aboutParagraphs: [
      'Sou Alioune Seck, desenvolvedor web freelance. Trabalho em projetos onde o código precisa fazer algo útil: reservar, gerir, acompanhar, vender ou economizar tempo.',
      'Já trabalhei em sites, ferramentas internas, integrações API e produtos próprios. Retomar um projeto existente costuma ser mais difícil do que começar do zero, e esse é justamente um tipo de trabalho que sei tratar.',
    ],
    aboutJourneyTitle: 'Percurso',
    aboutJourney: [
      {
        title: 'Fundador, StockScan',
        period: '2025 até hoje',
        text: 'SaaS de inventário online: scan, estoque, exportações, etiquetas e uso operacional.',
      },
      {
        title: 'Desenvolvedor web, LATELIER',
        period: 'Set. 2024 até Mai. 2025',
        text: 'Sites e ferramentas para PME, integrações API, SEO técnico e acessibilidade.',
      },
      {
        title: 'Desenvolvedor full-stack, IPI',
        period: 'Set. 2022 até Ago. 2023',
        text: 'Aplicação interna com React e Django REST.',
      },
      {
        title: 'Desenvolvedor frontend, Avisen',
        period: 'Mai. 2022 até Set. 2022',
        text: 'Integração Figma, responsivo e correções UI.',
      },
    ],
    aboutTodayTitle: 'Hoje',
    aboutToday:
      'Trabalho principalmente em projetos curtos ou bem definidos: terminar um app, corrigir uma ferramenta, adicionar reservas, conectar pagamento, publicar ou simplificar uma área admin.',
    aboutNote: 'Baseado na França · disponível à distância',
    closeAbout: 'Fechar',

    contactTitle: 'Conte-me o que você tem.',
    contactIntro:
      'Não precisa de um escopo completo. Um link, print, lista de bugs ou algumas linhas bastam para ver se posso ajudar.',
    phName: 'Seu nome',
    phEmail: 'Seu email',
    phPhone: 'Telefone ou WhatsApp (opcional)',
    phMessage: 'Explique o que você tem, o que não funciona, ou o que quer colocar online.',
    submit: 'Enviar mensagem',
    msgSuccess: 'Sua mensagem foi enviada. Você também receberá um email de confirmação.',
    msgError: 'Erro ao enviar o formulário. Verifique sua configuração do EmailJS.',

    navProjects: 'Projetos',
    navContact: 'Contato',
    footerIdentity:
      'Desenvolvedor web freelance · Aplicações web, retomada de existentes, ferramentas de negócio',
    footerNavTitle: 'Navegação',
    footerLegalTitle: 'Legal',
    footerContactTitle: 'Contato',
    footerPricing: 'Preços',
    footerServicesTitle: 'Serviços',
    footerServiceCreation: 'Criação de site',
    footerServiceBooking: 'Reservas e pagamento',
    footerServiceTakeover: 'Retomada de projeto',
    footerLocal: 'Programador web em Quimper',
    footerMentions: 'Aviso legal',
    footerPrivacy: 'Privacidade',
    footerCgu: 'Termos',
    footerSiren: 'SIREN: 995 288 438',
    footerApe: 'APE: 62.01Z',
    footerCopyright: '© 2026 Alioune Seck. Todos os direitos reservados.',
  },
};

const projectsByLang = {
  fr: [
    {
      title: 'Atelier d’Héloïse',
      type: 'Cas client · boutique & gestion métier',
      cover: '/atelier-dheloise.png',
      fallback: '/atelier-dheloise.png',
      demo: 'https://www.atelierdheloise.fr',
      caseStudy: '/realisations/atelier-dheloise',
      github: null,
      need:
        'Réunir dans un même outil la présentation de l’activité, les créations, les commandes, les prestations et l’administration quotidienne.',
      solution:
        'Boutique et catalogue, stock, commandes, réservation, paiements SumUp, devis et factures PDF, emails transactionnels, contenus administrables et back-office adapté à une utilisatrice non technique.',
      shows:
        'Un projet client structuré avec tests, CI, déploiement et documentation de reprise, pensé pour pouvoir être administré sans toucher au code.',
      stack: ['Next.js', 'Django', 'Wagtail', 'PostgreSQL', 'SumUp'],
    },
    {
      title: 'StockScan',
      type: 'SaaS d’inventaire',
      cover: '/previews/stockscan.png',
      fallback: '/inventorium.jpg',
      demo: 'https://www.stockscan.app',
      github: null,
      need:
        'Gérer un inventaire sans multiplier les fichiers, les vérifications manuelles et les oublis terrain.',
      solution:
        'Catalogue produits, scan code-barres, stock, TVA, DLC/DDM, pertes, étiquettes, exports PDF/CSV, statistiques et assistant IA. L’assistant intégré analyse les données et les fichiers importés pour aider à repérer les écarts, les doublons et les points à vérifier.',
      shows:
        'Un outil complet pour suivre l’inventaire, préparer les contrôles et garder une vision claire de l’activité.',
      stack: ['React', 'Django REST', 'Chart.js', 'html5-qrcode'],
    },
    {
      title: 'Samass',
      type: 'Site, réservation et assistant métier',
      cover: '/samass.jpg',
      fallback: '/samass.jpg',
      demo: 'https://samassbysam.com',
      github: 'https://github.com/Alioune4002/samass-clean',
      need:
        'Simplifier la prise de rendez-vous et éviter les échanges répétés par message.',
      solution:
        'Site responsive, réservation par créneaux, disponibilités, emails automatiques, interface admin et réponses locales aux questions fréquentes.',
      shows:
        'Un parcours plus simple pour le client, et moins de gestion manuelle côté praticien.',
      stack: ['Next.js', 'React', 'Django REST', 'SendGrid'],
    },
    {
      title: 'NUADYX',
      type: 'SaaS de réservation',
      cover: '/nuadyx.png',
      fallback: '/nuadyx.png',
      demo: 'https://www.nuadyx.com',
      github: null,
      need:
        'Centraliser la réservation, le paiement et le suivi pour des praticiens bien-être.',
      solution:
        'Créneaux, disponibilités, acomptes Stripe, avis clients, emails transactionnels et dashboard praticien.',
      shows:
        'Les réservations, paiements et confirmations restent au même endroit.',
      stack: ['Next.js', 'React', 'Django REST', 'PostgreSQL', 'Stripe'],
    },
  ],

  en: [
    {
      title: 'Atelier d’Héloïse',
      type: 'Client case · shop & business management',
      cover: '/atelier-dheloise.png',
      fallback: '/atelier-dheloise.png',
      demo: 'https://www.atelierdheloise.fr',
      caseStudy: '/realisations/atelier-dheloise',
      github: null,
      need:
        'Bring the business website, creations, orders, services and day-to-day administration into one tool.',
      solution:
        'Shop and catalog, stock, orders, booking, SumUp payments, quotes and PDF invoices, transactional emails, editable content and an admin area designed for a non-technical user.',
      shows:
        'A complete client project delivered with tests, CI, deployment and handover documentation, built to be managed without touching code.',
      stack: ['Next.js', 'Django', 'Wagtail', 'PostgreSQL', 'SumUp'],
    },
    {
      title: 'StockScan',
      type: 'Inventory SaaS',
      cover: '/previews/stockscan.png',
      fallback: '/inventorium.jpg',
      demo: 'https://www.stockscan.app',
      github: null,
      need:
        'Manage inventory without multiplying files, manual checks and missed field details.',
      solution:
        'Product catalog, barcode scan, stock, VAT, expiry dates, losses, labels, PDF/CSV exports, statistics and AI assistant. The integrated assistant analyzes data and imported files to help spot gaps, duplicates and points to check.',
      shows:
        'A complete tool to track inventory, prepare checks and keep a clear view of the activity.',
      stack: ['React', 'Django REST', 'Chart.js', 'html5-qrcode'],
    },
    {
      title: 'Samass',
      type: 'Website, booking and business assistant',
      cover: '/samass.jpg',
      fallback: '/samass.jpg',
      demo: 'https://samassbysam.com',
      github: 'https://github.com/Alioune4002/samass-clean',
      need:
        'Make appointment booking simpler and avoid repeated message exchanges.',
      solution:
        'Responsive website, slot booking, availability, automatic emails, admin interface and local answers to frequent questions.',
      shows:
        'A simpler flow for the client, and less manual management for the practitioner.',
      stack: ['Next.js', 'React', 'Django REST', 'SendGrid'],
    },
    {
      title: 'NUADYX',
      type: 'Booking SaaS',
      cover: '/nuadyx.png',
      fallback: '/nuadyx.png',
      demo: 'https://www.nuadyx.com',
      github: null,
      need:
        'Centralize booking, payment and follow-up for wellness practitioners.',
      solution:
        'Slots, availability, Stripe deposits, client reviews, transactional emails and practitioner dashboard.',
      shows:
        'Bookings, payments and confirmations stay in the same place.',
      stack: ['Next.js', 'React', 'Django REST', 'PostgreSQL', 'Stripe'],
    },
  ],

  pt: [
    {
      title: 'Atelier d’Héloïse',
      type: 'Caso de cliente · loja e gestão',
      cover: '/atelier-dheloise.png',
      fallback: '/atelier-dheloise.png',
      demo: 'https://www.atelierdheloise.fr',
      caseStudy: '/realisations/atelier-dheloise',
      github: null,
      need:
        'Reunir em uma única ferramenta a apresentação da atividade, criações, pedidos, serviços e administração diária.',
      solution:
        'Loja e catálogo, estoque, pedidos, reservas, pagamentos SumUp, orçamentos e faturas PDF, emails transacionais, conteúdo editável e back-office pensado para uma utilizadora não técnica.',
      shows:
        'Um projeto real de cliente entregue com testes, CI, publicação e documentação de passagem, administrável sem tocar no código.',
      stack: ['Next.js', 'Django', 'Wagtail', 'PostgreSQL', 'SumUp'],
    },
    {
      title: 'StockScan',
      type: 'SaaS de inventário',
      cover: '/previews/stockscan.png',
      fallback: '/inventorium.jpg',
      demo: 'https://www.stockscan.app',
      github: null,
      need:
        'Gerir inventário sem multiplicar arquivos, verificações manuais e esquecimentos no terreno.',
      solution:
        'Catálogo de produtos, scan de códigos de barras, estoque, IVA, validade, perdas, etiquetas, exportações PDF/CSV, estatísticas e assistente IA. O assistente integrado analisa dados e arquivos importados para ajudar a encontrar diferenças, duplicados e pontos a verificar.',
      shows:
        'Uma ferramenta completa para acompanhar o inventário, preparar controles e manter uma visão clara da atividade.',
      stack: ['React', 'Django REST', 'Chart.js', 'html5-qrcode'],
    },
    {
      title: 'Samass',
      type: 'Site, reservas e assistente de negócio',
      cover: '/samass.jpg',
      fallback: '/samass.jpg',
      demo: 'https://samassbysam.com',
      github: 'https://github.com/Alioune4002/samass-clean',
      need:
        'Simplificar a marcação de horários e evitar trocas repetidas por mensagem.',
      solution:
        'Site responsivo, reservas por horários, disponibilidade, emails automáticos, interface admin e respostas locais às perguntas frequentes.',
      shows:
        'Um percurso mais simples para o cliente, e menos gestão manual para o profissional.',
      stack: ['Next.js', 'React', 'Django REST', 'SendGrid'],
    },
    {
      title: 'NUADYX',
      type: 'SaaS de reservas',
      cover: '/nuadyx.png',
      fallback: '/nuadyx.png',
      demo: 'https://www.nuadyx.com',
      github: null,
      need:
        'Centralizar reservas, pagamento e acompanhamento para profissionais de bem-estar.',
      solution:
        'Horários, disponibilidade, sinais Stripe, avaliações de clientes, emails transacionais e dashboard do profissional.',
      shows:
        'Reservas, pagamentos e confirmações ficam no mesmo lugar.',
      stack: ['Next.js', 'React', 'Django REST', 'PostgreSQL', 'Stripe'],
    },
  ],
};

function NotFound() {
  const { lang } = useLang();
  const copy404 = {
    fr: {
      eyebrow: '404',
      title: 'Cette page n’existe pas.',
      text: 'Le lien est peut-être ancien ou l’adresse a été modifiée.',
      cta: 'Retour au portfolio',
    },
    en: {
      eyebrow: '404',
      title: 'This page does not exist.',
      text: 'The link may be outdated or the address may have changed.',
      cta: 'Back to portfolio',
    },
    pt: {
      eyebrow: '404',
      title: 'Esta página não existe.',
      text: 'O link pode estar desatualizado ou o endereço pode ter mudado.',
      cta: 'Voltar ao portfólio',
    },
  };
  const t = copy404[lang] || copy404.fr;

  useEffect(() => {
    const previousTitle = document.title;
    const robots = document.createElement('meta');
    robots.setAttribute('name', 'robots');
    robots.setAttribute('content', 'noindex, nofollow');
    document.head.appendChild(robots);
    document.title = `404 | Alioune Seck`;

    return () => {
      document.title = previousTitle;
      robots.remove();
    };
  }, []);

  return (
    <main className="pricing-page">
      <header className="pricing-hero">
        <div className="hero-tags pricing-tags"><span>{t.eyebrow}</span></div>
        <h1>{t.title}</h1>
        <p>{t.text}</p>
        <div className="project-actions">
          <Link to="/" className="primary-cta">{t.cta}</Link>
        </div>
      </header>
    </main>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const canonicalUrl = `https://alioune-seck.dev${pathname === '/' ? '' : pathname}`;
    let canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }

    canonical.setAttribute('href', canonicalUrl);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', canonicalUrl);

    if (hash) {
      requestAnimationFrame(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  return null;
}

// eslint-disable-next-line no-unused-vars
function PortfolioPage() {
  const { lang, setLang } = useLang();
  const t = copy[lang] || copy.fr;
  const projects = projectsByLang[lang] || projectsByLang.fr;
  const form = useRef(null);
  const [showMessage, setShowMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const heroSlides = t.heroSlides || [];
  const heroSlide = heroSlides[heroIndex] || heroSlides[0];
  const servicePaths = [
    '/services/creation-site-web',
    '/services/reservation-paiement-backoffice',
    '/services/reprise-projet-web',
  ];
  const { register, handleSubmit, reset } = useForm();

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches || !heroSlides.length) return undefined;

    const timer = window.setInterval(() => {
      setHeroIndex((current) => (current + 1) % heroSlides.length);
    }, 5600);

    return () => window.clearInterval(timer);
  }, [heroSlides.length]);

  useEffect(() => {
    setHeroIndex(0);
  }, [lang]);

  useEffect(() => {
    if (!showMessage) return undefined;
    const timer = setTimeout(() => setShowMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [showMessage]);

  useEffect(() => {
    const savedMessage = sessionStorage.getItem('prefilledContactMessage');
    if (!savedMessage) return;

    reset({ message: savedMessage });
    sessionStorage.removeItem('prefilledContactMessage');

    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 250);
  }, [reset]);

  useEffect(() => {
    if (!isAboutOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsAboutOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.classList.add('modal-open');

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('modal-open');
    };
  }, [isAboutOpen]);

  const openProject = (project) => {
    if (typeof window === 'undefined') return;

    if (project.caseStudy) {
      window.location.assign(project.caseStudy);
      return;
    }

    if (project.demo) {
      window.open(project.demo, '_blank', 'noopener,noreferrer');
    }
  };

  const onSubmit = () => {
    if (new FormData(form.current).get('honeypot')) return;

    setIsLoading(true);

    const formData = new FormData(form.current);
    const SERVICE_ID = 'service_u5azogu';
    const TEMPLATE_TO_YOU_ID = 'template_p284e2a';
    const TEMPLATE_AUTOREPLY_ID = 'template_ruoibdc';
    const PUBLIC_KEY = '7iv4FE3KzxDAAepum';

    emailjs
      .sendForm(SERVICE_ID, TEMPLATE_TO_YOU_ID, form.current, PUBLIC_KEY)
      .then(
        () => {
          setShowMessage('success');
          reset();
        },
        () => setShowMessage('error')
      )
      .finally(() => setIsLoading(false));

    emailjs
      .send(
        SERVICE_ID,
        TEMPLATE_AUTOREPLY_ID,
        {
          name: formData.get('name'),
          email: formData.get('email'),
          phone: formData.get('phone'),
          message: formData.get('message'),
        },
        PUBLIC_KEY
      )
      .catch(() => setShowMessage('error'));
  };

  return (
    <div className="portfolio">
      <header className="site-header" aria-label="Navigation principale">
        <div className="header-shell">
          <a href="#top" className="brand-lockup">
            <span className="brand-mark" aria-hidden="true">
              <img
                src="/icon_alioune.png"
                alt=""
                className="brand-logo"
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            </span>
            <span className="brand-copy">
              <strong>
                <span className="brand-accent">Alioune</span> Seck
              </strong>
              <small>{t.brandRole}</small>
            </span>
          </a>

          <nav className="header-nav">
            <a href="#projects">{t.navProjects}</a>
            <a href="#contact">{t.navContact}</a>
            <Link to="/pricing">{t.footerPricing}</Link>
          </nav>

          <div className="lang-switcher" aria-label={t.langLabel}>
            {languages.map((language) => (
              <button
                key={language}
                className={`lang-btn ${lang === language ? 'active' : ''}`}
                onClick={() => setLang(language)}
                type="button"
                aria-current={lang === language ? 'true' : undefined}
              >
                {language.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main id="top">
        <section className="hero-section hero-carousel" aria-label={t.brandRole}>
          <AnimatePresence mode="wait">
            <motion.div
              className="hero-slide"
              key={`${lang}-${heroIndex}`}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.38, ease: 'easeOut' }}
            >
              <div
                className="hero-slide-visual"
                style={{ backgroundImage: `url(${heroSlide?.image})` }}
                aria-hidden="true"
              />
              <div className="hero-slide-overlay" aria-hidden="true" />

              <div className="hero-copy hero-slide-copy">
                <span className="hero-eyebrow">{heroSlide?.eyebrow}</span>
                <h1>{heroSlide?.title}</h1>
                <p className="hero-subtitle">{heroSlide?.text}</p>
                <p className="hero-micro">{heroSlide?.micro}</p>

                <div className="hero-actions">
                  <a href="#contact" className="primary-cta">
                    {heroSlide?.cta}
                  </a>
                  <a href="#projects" className="ghost-cta">
                    {t.ctaProjects}
                  </a>
                  <Link to={servicePaths[heroIndex]} className="ghost-cta">
                    {t.ctaService}
                  </Link>
                  <button
                    type="button"
                    className="ghost-cta"
                    onClick={() => setIsAboutOpen(true)}
                  >
                    {t.ctaAbout}
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="hero-pagination" aria-label="Navigation des services">
            {heroSlides.map((slide, index) => (
              <button
                type="button"
                key={slide.eyebrow}
                className={index === heroIndex ? 'active' : ''}
                aria-label={slide.eyebrow}
                aria-current={index === heroIndex ? 'true' : undefined}
                onClick={() => setHeroIndex(index)}
              >
                <span>{String(index + 1).padStart(2, '0')}</span>
                <i aria-hidden="true" />
              </button>
            ))}
          </div>
        </section>

        <section className="section-block projects-section" id="projects">
          <div className="section-head">
            <h2>{t.projectsTitle}</h2>
            <p>{t.projectsIntro}</p>
          </div>

          <div className="project-list">
            {projects.map((project) => (
              <motion.article
                key={project.title}
                className="project-card"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.24 }}
                role="link"
                tabIndex={0}
                onClick={(event) => {
                  if (event.target.closest('.project-actions')) return;
                  openProject(project);
                }}
                onKeyDown={(event) => {
                  if (event.target.closest('.project-actions')) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openProject(project);
                  }
                }}
              >
                <img
                  src={project.cover}
                  alt={`Aperçu du projet ${project.title}`}
                  className="project-cover"
                  loading="lazy"
                  onError={(event) => {
                    if (project.fallback && event.currentTarget.dataset.fallback !== 'done') {
                      event.currentTarget.dataset.fallback = 'done';
                      event.currentTarget.src = project.fallback;
                    }
                  }}
                />

                <div className="project-content">
                  <div className="project-title-row">
                    <h3>{project.title}</h3>
                    <span>{project.type}</span>
                  </div>

                  <div className="project-mini-grid">
                    <div>
                      <strong>{t.needLabel}</strong>
                      <p>{project.need}</p>
                    </div>
                    <div>
                      <strong>{t.solutionLabel}</strong>
                      <p>{project.solution}</p>
                    </div>
                    <div>
                      <strong>{t.showsLabel}</strong>
                      <p>{project.shows}</p>
                    </div>
                  </div>

                  <div className="project-stack" aria-label={`${t.stackLabel} ${project.title}`}>
                    {project.stack.map((item) => (
                      <span key={item}>{item}</span>
                    ))}
                  </div>

                  <div className="project-actions">
                    {project.caseStudy && (
                      <Link
                        to={project.caseStudy}
                        className="text-button strong"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {t.btnCaseStudy}
                      </Link>
                    )}
                    <a
                      href={project.demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-button strong"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {t.btnDemo}
                    </a>
                    {project.github && (
                      <a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-button"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {t.btnGithub}
                      </a>
                    )}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        <section className="section-block situations-section">
          <div className="section-head">
            <h2>{t.situationsTitle}</h2>
            <p>{t.situationsIntro}</p>
          </div>

          <div className="situation-grid">
            {t.situations.map((situation) => (
              <article className="small-card" key={situation.title}>
                <h3>{situation.title}</h3>
                <p>{situation.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section-block tech-section">
          <div className="section-head">
            <h2>{t.techTitle}</h2>
            <p>{t.techIntro}</p>
          </div>

          <div className="tech-groups">
            {t.techGroups.map((group) => (
              <article className="tech-group" key={group.title}>
                <h3>{group.title}</h3>
                <div className="tag-list">
                  {group.items.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="section-block contact-section" id="contact">
          <div className="section-head">
            <h2>{t.contactTitle}</h2>
            <p>{t.contactIntro}</p>
          </div>

          <form ref={form} className="contact-form" onSubmit={handleSubmit(onSubmit)}>
            <input
              type="text"
              name="honeypot"
              style={{ display: 'none' }}
              {...register('honeypot')}
            />
            <input {...register('name', { required: true })} placeholder={t.phName} name="name" />
            <input
              {...register('email', { required: true })}
              placeholder={t.phEmail}
              type="email"
              name="email"
            />
            <input
              {...register('phone')}
              placeholder={t.phPhone}
              type="tel"
              name="phone"
            />
            <textarea
              {...register('message', { required: true })}
              placeholder={t.phMessage}
              name="message"
            />
            <button type="submit" disabled={isLoading}>
              {isLoading ? <span className="spinner"></span> : t.submit}
            </button>
          </form>

          {showMessage && (
            <motion.div
              className={`contact-message ${showMessage === 'success' ? 'success' : 'error'}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22 }}
            >
              {showMessage === 'success' ? t.msgSuccess : t.msgError}
            </motion.div>
          )}
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-card">
          <div className="footer-brand-column">
            <a href="#top" className="brand-lockup footer-brand-lockup">
              <span className="brand-mark" aria-hidden="true">
                <img
                  src="/icon_alioune.png"
                  alt=""
                  className="brand-logo"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                />
              </span>
              <span className="brand-copy">
                <strong>
                  <span className="brand-accent">Alioune</span> Seck
                </strong>
                <small>{t.brandRole}</small>
              </span>
            </a>
            <p>{t.footerIdentity}</p>
          </div>

          <div className="footer-main">
            <nav className="footer-col" aria-label={t.footerNavTitle}>
              <h4>{t.footerNavTitle}</h4>
              <a href="#projects">{t.navProjects}</a>
              <a href="#contact">{t.navContact}</a>
              <Link to="/pricing">{t.footerPricing}</Link>
            </nav>

            <nav className="footer-col" aria-label={t.footerServicesTitle}>
              <h4>{t.footerServicesTitle}</h4>
              <Link to="/services/creation-site-web">{t.footerServiceCreation}</Link>
              <Link to="/services/reservation-paiement-backoffice">{t.footerServiceBooking}</Link>
              <Link to="/services/reprise-projet-web">{t.footerServiceTakeover}</Link>
              <Link to="/developpeur-web-quimper">{t.footerLocal}</Link>
            </nav>

            <nav className="footer-col" aria-label={t.footerLegalTitle}>
              <h4>{t.footerLegalTitle}</h4>
              <Link to="/mentions-legales">{t.footerMentions}</Link>
              <Link to="/politique-confidentialite">{t.footerPrivacy}</Link>
              <Link to="/cgu">{t.footerCgu}</Link>
            </nav>

            <div className="footer-col">
              <h4>{t.footerContactTitle}</h4>
              <a href="mailto:contact@alioune-seck.dev">contact@alioune-seck.dev</a>
              <span>{t.footerSiren}</span>
              <span>{t.footerApe}</span>
            </div>
          </div>

          <div className="footer-bottom">
            <span>{t.footerCopyright}</span>
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {isAboutOpen && (
          <motion.div
            className="about-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => setIsAboutOpen(false)}
          >
            <motion.aside
              className="about-drawer"
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.22 }}
              onClick={(event) => event.stopPropagation()}
              aria-modal="true"
              role="dialog"
              aria-labelledby="about-title"
            >
              <button
                className="about-close"
                type="button"
                onClick={() => setIsAboutOpen(false)}
                aria-label={t.closeAbout}
              >
                ×
              </button>
              <img src="/profile.jpg" alt="Alioune Seck" className="about-photo" />
              <h2 id="about-title">{t.aboutTitle}</h2>
              {t.aboutParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              <div className="about-section">
                <h3>{t.aboutJourneyTitle}</h3>
                <div className="about-timeline">
                  {t.aboutJourney.map((item) => (
                    <article className="about-timeline-item" key={item.title}>
                      <div>
                        <strong>{item.title}</strong>
                        <span>{item.period}</span>
                      </div>
                      <p>{item.text}</p>
                    </article>
                  ))}
                </div>
              </div>
              <div className="about-section">
                <h3>{t.aboutTodayTitle}</h3>
                <p>{t.aboutToday}</p>
              </div>
              <div className="about-note">{t.aboutNote}</div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AnalyticsConsent />
        <Routes>
          <Route path="/" element={<PortfolioV2 />} />
          <Route path="/pricing" element={<PricingV2 />} />
          <Route path="/realisations/atelier-dheloise" element={<AtelierDHeloise />} />
          <Route path="/estimer-mon-projet" element={<EstimatorPage />} />
          <Route path="/services/creation-site-web" element={<ServicePage service="creation" />} />
          <Route path="/services/reservation-paiement-backoffice" element={<ServicePage service="booking" />} />
          <Route path="/services/reprise-projet-web" element={<ServicePage service="takeover" />} />
          <Route path="/developpeur-web-quimper" element={<DeveloppeurWebQuimper />} />
          <Route path="/developpeur-web-freelance-france" element={<DeveloppeurWebFrance />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/cgv" element={<CGV />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
